import { useParams, Link, useNavigate } from "react-router";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import { useDeleteOrderMutation, useUpdateOrderStatusMutation } from "@/lib/mutations";
import { useOrderQuery } from "@/lib/queries";
import { cn } from "@/lib/utils";

const statusFlow = ["pending", "confirmed", "completed"];

export function OrderDetail() {
  const { id } = useParams();
  const orderId = Number(id);
  const navigate = useNavigate();
  const { data: order, isPending, isError, error, refetch } = useOrderQuery(orderId, { enabled: !!id });
  const updateStatusMutation = useUpdateOrderStatusMutation();
  const deleteOrderMutation = useDeleteOrderMutation();

  const updateStatus = async (status: string) => {
    try {
      await updateStatusMutation.mutateAsync({ id: orderId, status });
      toast.success(`Status updated to ${status}`);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  const deleteOrder = async () => {
    if (!confirm("Delete this order?")) return;
    try {
      await deleteOrderMutation.mutateAsync(orderId);
      toast.success("Order deleted");
      navigate("/orders");
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!id || !Number.isFinite(orderId) || orderId <= 0) {
    return (
      <div className="flex h-48 items-center justify-center">
        <p className="text-[13px] text-muted-foreground">Invalid order ID.</p>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-48 flex-col items-center justify-center gap-3">
        <p className="text-[13px] text-muted-foreground">{getErrorMessage(error)}</p>
        <button
          onClick={() => refetch()}
          className="inline-flex h-8 items-center rounded-md border px-3 text-[12px] font-medium transition-colors hover:bg-muted"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex h-48 items-center justify-center">
        <p className="text-[13px] text-muted-foreground">Order not found.</p>
      </div>
    );
  }

  const statusStyles: Record<string, string> = {
    pending: "bg-foreground/5 text-foreground/70",
    confirmed: "bg-foreground/10 text-foreground/90",
    completed: "bg-foreground text-background",
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <Link
          to="/orders"
          className="mb-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-3" />
          Back to orders
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-[22px] font-semibold tracking-tight">
            Order <span className="font-mono">#{order.id}</span>
          </h1>
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
              statusStyles[order.status] ?? statusStyles.pending
            }`}
          >
            {order.status}
          </span>
        </div>
      </div>

      {/* Details card */}
      <div className="overflow-hidden rounded-lg border">
        {/* Customer info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 p-5">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Customer</p>
            <p className="mt-1 text-[14px] font-medium">{order.customer_name}</p>
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Email</p>
            <p className="mt-1 text-[14px]">{order.customer_email}</p>
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Phone</p>
            <p className="mt-1 text-[14px]">{order.customer_phone}</p>
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Pickup date</p>
            <p className="mt-1 text-[14px]">{order.pickup_date}</p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t" />

        {/* Item */}
        <div className="p-5">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Item ordered</p>
          <p className="mt-1 text-[14px] font-medium">{order.item_name}</p>
          {order.notes && (
            <div className="mt-4">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Notes</p>
              <p className="mt-1 text-[13px] text-muted-foreground leading-relaxed">{order.notes}</p>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t" />

        {/* Status actions */}
        <div className="flex flex-wrap items-center gap-2 p-5">
          <span className="text-[12px] font-medium text-muted-foreground mr-2">Update status:</span>
          {statusFlow
            .filter((s) => s !== order.status)
            .map((s) => (
              <button
                key={s}
                onClick={() => updateStatus(s)}
                disabled={updateStatusMutation.isPending}
                className="rounded-md px-3 py-1.5 text-[12px] font-medium transition-all disabled:cursor-not-allowed disabled:opacity-60 bg-foreground/5 text-foreground hover:bg-foreground hover:text-background"
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          <button
            onClick={deleteOrder}
            disabled={deleteOrderMutation.isPending}
            className="ml-auto rounded-md border border-red-200 px-3 py-1.5 text-[12px] font-medium text-red-600 transition-all hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
